import React from 'react';
import styled from 'styled-components';
import { Card, Button, Tag, Dropdown, Menu, Tooltip } from 'antd';
import { CloseOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../i18n/useTranslation';

import { ReactComponent as DownloadOutlined } from '../../../images/downloaded-outline.svg';
import { ReactComponent as DeleteOutlined } from '../../../images/delete-outline.svg';
import { ReactComponent as DragIndicator } from '../../../images/drag-indicator.svg';

export interface ActionAbleItems {
    key: string;
    name: string;
    onClick: () => void;
    prefixIcon?: React.FC<any>;
    dropDownActionAbleItems?: ActionAbleItems[];
    suffixIcon?: React.FC<any>;
    disabled?: boolean;
    disabledTooltip?: string;
}

interface Props {
    type?: string;
    hideExport?: boolean;
    selectedItems: string[];
    exportCsv?: () => void;
    confirmDatasetDeletion?: () => void;
    confirmGlossaryTermsDeletion?: () => void;
    clearSelectedItems: () => void;
    onRemove?: () => void;
    actionAbleItems?: ActionAbleItems[];
    /** When false, disable Export button (dataset type). */
    canExport?: boolean;
    /** When false, disable Delete button (dataset type). */
    canDelete?: boolean;
    /** Tooltip when action is disabled due to RBAC. */
    unauthorisedTooltip?: string;
}

const Flex = styled.div`
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    max-width: 100%;
`;

const DeleteButton = styled(Button)`
    background-color: white !important;
    span {
        color: #d96758 !important;
    }
`;

const ExportCSVButton = styled(Button)`
    background-color: white !important;
    span {
        color: #5f767c !important;
    }
`;

const ActionAbleButton = styled(Button)`
    background-color: white !important;
    span {
        color: #5f767c !important;
    }
`;

const Layout = styled.div`
    display: flex;
    gap: 8px;
`;

const DATASET = 'dataset';
const GLOSSARY = 'glossary';
const GLOSSARY_TERM_TAB = 'glossaryTermTab';

export const BulkAction = ({
    selectedItems,
    type,
    exportCsv,
    hideExport,
    confirmDatasetDeletion,
    confirmGlossaryTermsDeletion,
    clearSelectedItems,
    onRemove,
    actionAbleItems,
    canExport = true,
    canDelete = true,
    unauthorisedTooltip,
}: Props) => {
    const isDataset = type === DATASET;
    const isGlossary = type === GLOSSARY;
    const isGlossaryTermTab = type === GLOSSARY_TERM_TAB;
    const { t } = useTranslation();

    // To check if the user has the required privilege to export or delete
    const applyRbacExport = (isDataset || isGlossary) && canExport;
    const applyRbacDelete = (isDataset || isGlossary) && canDelete;

    const handleDeleteClick = () => {
        if (!applyRbacDelete) return;
        if (isDataset) confirmDatasetDeletion?.();
        else confirmGlossaryTermsDeletion?.();
    };

    return selectedItems?.length ? (
        <Card className="f-card f-bulk-action-bar">
            <Layout className="pa-2">
                <Flex>
                    <Flex className="shrink">
                        <DragIndicator height={20} width={20} color="#BCBDBF" />
                    </Flex>
                    <Layout>
                        <Flex className="f-text-big-content f-color-blue-s100 f-text-semibold shrink">
                            <Tag className="grey-tag">
                                <span>{selectedItems.length}</span>
                            </Tag>
                        </Flex>
                        <Flex className="f-text-big-content f-color-dark-black-s100 flex">{t('Selected')}</Flex>
                    </Layout>
                </Flex>
                {actionAbleItems && actionAbleItems.length > 0 ? (
                    <>
                        {actionAbleItems.map((actionAbleItem) => (
                            <Flex key={actionAbleItem.key} className="shrink">
                                {actionAbleItem.dropDownActionAbleItems ? (
                                    <Tooltip
                                        title={actionAbleItem.disabled ? actionAbleItem.disabledTooltip : undefined}
                                        overlayClassName="f-tooltip-white"
                                    >
                                        <span>
                                            <Dropdown
                                                trigger={['click']}
                                                placement="bottomRight"
                                                disabled={actionAbleItem.disabled}
                                                overlay={
                                                    <Menu className="f-dropdown-small">
                                                        {actionAbleItem.dropDownActionAbleItems.map((dropDownItem) => (
                                                            <Tooltip
                                                                key={dropDownItem.key}
                                                                title={
                                                                    dropDownItem.disabled
                                                                        ? dropDownItem.disabledTooltip
                                                                        : undefined
                                                                }
                                                                overlayClassName="f-tooltip-white"
                                                                placement="left"
                                                            >
                                                                <span>
                                                                    <Menu.Item
                                                                        key={dropDownItem.key}
                                                                        onClick={
                                                                            dropDownItem.disabled
                                                                                ? undefined
                                                                                : dropDownItem.onClick
                                                                        }
                                                                        disabled={dropDownItem.disabled}
                                                                    >
                                                                        {dropDownItem.name}
                                                                    </Menu.Item>
                                                                </span>
                                                            </Tooltip>
                                                        ))}
                                                    </Menu>
                                                }
                                            >
                                                <ActionAbleButton
                                                    onClick={
                                                        actionAbleItem.disabled ? undefined : actionAbleItem.onClick
                                                    }
                                                    disabled={actionAbleItem.disabled}
                                                    icon={
                                                        actionAbleItem.prefixIcon && (
                                                            <actionAbleItem.prefixIcon
                                                                style={{ width: 16 }}
                                                                color="#d96758"
                                                            />
                                                        )
                                                    }
                                                >
                                                    {actionAbleItem.name}
                                                    {actionAbleItem.suffixIcon && <actionAbleItem.suffixIcon />}
                                                </ActionAbleButton>
                                            </Dropdown>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        title={actionAbleItem.disabled ? actionAbleItem.disabledTooltip : undefined}
                                        overlayClassName="f-tooltip-white"
                                    >
                                        <span>
                                            <ActionAbleButton
                                                onClick={actionAbleItem.disabled ? undefined : actionAbleItem.onClick}
                                                disabled={actionAbleItem.disabled}
                                                icon={
                                                    actionAbleItem.prefixIcon ? (
                                                        <actionAbleItem.prefixIcon
                                                            style={{ width: 16 }}
                                                            color="#d96758"
                                                        />
                                                    ) : (
                                                        <></>
                                                    )
                                                }
                                            >
                                                {actionAbleItem.name}
                                            </ActionAbleButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Flex>
                        ))}
                    </>
                ) : (
                    <>
                        {!hideExport && (
                            <Flex className="shrink">
                                <Tooltip
                                    title={!applyRbacExport ? unauthorisedTooltip : undefined}
                                    overlayClassName="f-tooltip-white"
                                >
                                    <span>
                                        <ExportCSVButton
                                            onClick={!applyRbacExport ? undefined : exportCsv}
                                            disabled={!applyRbacExport}
                                            icon={<DownloadOutlined style={{ width: 16 }} color="#5F767C" />}
                                        >
                                            {t('Export')}
                                        </ExportCSVButton>
                                    </span>
                                </Tooltip>
                            </Flex>
                        )}

                        {!isGlossaryTermTab && (
                            <Flex className="shrink">
                                <Tooltip
                                    title={!applyRbacDelete ? unauthorisedTooltip : undefined}
                                    overlayClassName="f-tooltip-white"
                                >
                                    <span>
                                        <DeleteButton
                                            danger
                                            disabled={!applyRbacDelete}
                                            onClick={handleDeleteClick}
                                            icon={<DeleteOutlined color="#d96758" style={{ width: 10 }} />}
                                        >
                                            {isDataset ? t('Delete Datasets') : t('Delete')}
                                        </DeleteButton>
                                    </span>
                                </Tooltip>
                            </Flex>
                        )}
                    </>
                )}

                {isGlossaryTermTab && (
                    <Flex className="shrink">
                        <ExportCSVButton
                            onClick={onRemove}
                            icon={<MinusCircleOutlined style={{ width: 10 }} className="mr-1" />}
                        >
                            {t('Remove Relationship')}
                        </ExportCSVButton>
                    </Flex>
                )}

                <Flex className="shrink">
                    <Button
                        type="text"
                        shape="circle"
                        onClick={clearSelectedItems}
                        icon={<CloseOutlined style={{ width: 14 }} />}
                    />
                </Flex>
            </Layout>
        </Card>
    ) : (
        <></>
    );
};
